import React from "react";

/**
 * Opacity applied to each shade "role" of the periscope mark. The mark is
 * recoloured to solid white; these opacities preserve the original shading so
 * it reads as a layered white logo rather than a flat silhouette.
 */
export type TileOpacities = {
  /** Dominant rings, prongs, outer ring (was #6559ff). */
  brand: number;
  /** Solid inner disc (was #4740d4). */
  deep: number;
  /** Thin outline rings (was #c2c5ff). */
  pale: number;
  /** Small dot (was #fff). */
  dot: number;
};

interface PeriscopeTileIconProps {
  /** Overall tile size in px (square). Production sidenav size is 20. */
  size?: number;
  /** Tile background colour. Defaults to the brand purple. */
  tileColor?: string;
  opacities: TileOpacities;
  style?: React.CSSProperties;
  className?: string;
}

// Native viewBox of the periscope mark (from periscope-svg.tsx).
const LOGO_VIEWBOX = 250.3;
// Fraction of the tile reserved as padding around the mark on each side.
const PADDING_RATIO = 0.14;
// Corner radius as a fraction of the tile size.
const RADIUS_RATIO = 0.22;
// Shared translate used by the original mark's paths.
const LOGO_TRANSFORM = "translate(-16.12 -11.94)";

/**
 * Periscope logo rendered in white on a rounded, coloured tile ("app-icon"
 * treatment). Reuses the exact geometry of {@link PeriscopeSvg}; only the fills
 * (white + per-role opacity) and the added background tile differ.
 */
export function PeriscopeTileIcon({
  size = 20,
  tileColor = "#6559ff",
  opacities,
  style,
  className,
}: PeriscopeTileIconProps) {
  const radius = LOGO_VIEWBOX * RADIUS_RATIO;
  // Scale + centre the mark within the padded area of the tile.
  const scale = 1 - PADDING_RATIO * 2;
  const offset = (LOGO_VIEWBOX * PADDING_RATIO) / scale;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${LOGO_VIEWBOX} ${LOGO_VIEWBOX}`}
      width={size}
      height={size}
      style={style}
      className={className}
    >
      <rect
        width={LOGO_VIEWBOX}
        height={LOGO_VIEWBOX}
        rx={radius}
        ry={radius}
        fill={tileColor}
      />
      <g transform={`scale(${scale}) translate(${offset} ${offset})`}>
        <circle
          fill="#fff"
          fillOpacity={opacities.deep}
          cx="127.84"
          cy="127.51"
          r="55.59"
        />
        <path
          fill="#fff"
          fillOpacity={opacities.brand}
          d="M144,195a55.59,55.59,0,1,1,55.59-55.59A55.66,55.66,0,0,1,144,195Zm0-107.68a52.09,52.09,0,1,0,52.09,52.09A52.15,52.15,0,0,0,144,87.36Z"
          transform={LOGO_TRANSFORM}
        />
        <path
          fill="#fff"
          fillOpacity={opacities.pale}
          d="M144,197.86a58.41,58.41,0,1,1,58.41-58.41A58.47,58.47,0,0,1,144,197.86Zm0-107.68a49.28,49.28,0,1,0,49.27,49.27A49.33,49.33,0,0,0,144,90.18Z"
          transform={LOGO_TRANSFORM}
        />
        <path
          fill="#fff"
          fillOpacity={opacities.brand}
          d="M144,163.75a24.3,24.3,0,1,1,24.3-24.3A24.33,24.33,0,0,1,144,163.75Zm0-47.07a22.77,22.77,0,1,0,22.77,22.77A22.8,22.8,0,0,0,144,116.68Z"
          transform={LOGO_TRANSFORM}
        />
        <path
          fill="#fff"
          fillOpacity={opacities.pale}
          d="M144,166.57a27.12,27.12,0,1,1,27.11-27.12A27.15,27.15,0,0,1,144,166.57Zm0-47.07a20,20,0,1,0,19.95,19.95A20,20,0,0,0,144,119.5Z"
          transform={LOGO_TRANSFORM}
        />
        <path
          fill="#fff"
          fillOpacity={opacities.brand}
          d="M144,257.56A118.11,118.11,0,1,1,262.07,139.45,118.25,118.25,0,0,1,144,257.56Zm0-203.24a85.13,85.13,0,1,0,85.12,85.13A85.23,85.23,0,0,0,144,54.32Z"
          transform={LOGO_TRANSFORM}
        />
        <path
          fill="#fff"
          fillOpacity={opacities.brand}
          d="M62.37,98,16.12,76.86A134.05,134.05,0,0,1,52.85,29.41L85.16,68.67A84.35,84.35,0,0,0,62.37,98Z"
          transform={LOGO_TRANSFORM}
        />
        <path
          fill="#fff"
          fillOpacity={opacities.brand}
          d="M223.44,90.65c-7.88-12.45-24-27.8-34.57-32.84L210.8,11.94c21.67,10.35,44.38,33.76,55.61,51.53Z"
          transform={LOGO_TRANSFORM}
        />
        <path
          fill="#fff"
          fillOpacity={opacities.brand}
          d="M227.45,255.94l-29.18-41.65c9.42-6.6,18.23-15.74,23-23.85l43.89,25.67C256.68,230.56,243,245.08,227.45,255.94Z"
          transform={LOGO_TRANSFORM}
        />
        <path
          fill="#fff"
          fillOpacity={opacities.brand}
          d="M93.39,271.43C61.59,261,32.71,234.54,18,202.38l46.26-21.13c8.84,19.37,26.49,35.81,45,41.86Z"
          transform={LOGO_TRANSFORM}
        />
        <circle
          fill="#fff"
          fillOpacity={opacities.dot}
          cx="145.18"
          cy="113.45"
          r="14.01"
        />
      </g>
    </svg>
  );
}

/** Variant opacity presets for the temporary icon exploration. */
export const PERISCOPE_TILE_VARIANTS: ReadonlyArray<{
  label: string;
  description: string;
  opacities: TileOpacities;
}> = [
  {
    label: "A",
    description: "White 100 / 75 / 50%",
    opacities: { brand: 1, deep: 0.75, pale: 0.5, dot: 1 },
  },
  {
    label: "B",
    description: "White 100 / 85 / 70%",
    opacities: { brand: 1, deep: 0.85, pale: 0.7, dot: 1 },
  },
  {
    label: "C",
    description: "Flat white (control)",
    opacities: { brand: 1, deep: 1, pale: 1, dot: 1 },
  },
];
